import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import { ErrorState, LoadingState, NotFoundPage, PageContainer } from '@/shared/ui';

import {
  useActivateCampaign,
  useArchiveCampaign,
  useBudgetBurn,
  useCampaign,
  useEndCampaign,
  usePauseCampaign,
  usePromotions,
  useUpdateCampaign,
} from '../../hooks';
import { formatDate } from '../../lib/format';
import { usePromoPermissions } from '../../lib/usePromoPermissions';
import { useToast } from '../../lib/useToast';
import { CAMPAIGN_STATUS_COLOR, PROMOTION_STATUS_COLOR } from '../../model/statuses';
import { BudgetBurnBar } from '../components/BudgetBurnBar';
import { CampaignFormDialog } from '../components/CampaignFormDialog';

/** "Campaign" — campaign detail (plan-011 §8.2). */
export function CampaignDetailPage() {
  const { t } = useTranslation('promotions');
  const navigate = useNavigate();
  const { campaignId } = useParams<{ campaignId: string }>();
  const { canManage } = usePromoPermissions();
  const { toast, success, error: notifyError, clear } = useToast();

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [pauseConfirmOpen, setPauseConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'end' | 'archive' | null>(null);

  const campaignQuery = useCampaign(campaignId);
  const promotions = usePromotions({ size: 200 });
  const budgetBurn = useBudgetBurn(campaignQuery.data?.budgetId ?? undefined, {
    enabled: Boolean(campaignQuery.data?.budgetId),
  });

  const updateMutation = useUpdateCampaign();
  const activateMutation = useActivateCampaign();
  const pauseMutation = usePauseCampaign();
  const endMutation = useEndCampaign();
  const archiveMutation = useArchiveCampaign();

  if (campaignQuery.isLoading) {
    return (
      <PageContainer>
        <LoadingState label={t('campaignDetail.loading')} fullHeight />
      </PageContainer>
    );
  }
  if (campaignQuery.isError) {
    if (campaignQuery.error?.status === 404) return <NotFoundPage />;
    return (
      <PageContainer>
        <ErrorState
          title={t('campaignDetail.loadError.title')}
          message={campaignQuery.error?.message ?? t('common:error.unknown')}
          onRetry={() => void campaignQuery.refetch()}
        />
      </PageContainer>
    );
  }

  const campaign = campaignQuery.data;
  if (!campaign) return <NotFoundPage />;

  const campaignPromotions = (promotions.data?.content ?? []).filter((p) => p.campaignId === campaign.id);
  const canEnd = campaign.status === 'ACTIVE' || campaign.status === 'PAUSED' || campaign.status === 'SCHEDULED';

  return (
    <PageContainer>
      <Button
        component={RouterLink}
        to="/promo/calendar"
        startIcon={<ArrowBackRoundedIcon sx={{ fontSize: 16 }} />}
        size="small"
        color="inherit"
        sx={{ alignSelf: 'flex-start', color: 'text.secondary', mb: 2 }}
      >
        {t('campaignDetail.backLink')}
      </Button>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {campaign.name}
        </Typography>
        <Chip size="small" label={campaign.code} sx={{ fontFamily: 'JetBrains Mono, monospace' }} />
        <Chip size="small" color={CAMPAIGN_STATUS_COLOR[campaign.status]} label={campaign.status} />
        <Box sx={{ flex: 1 }} />
        {canManage ? (
          <Stack direction="row" spacing={1}>
            {campaign.status === 'ACTIVE' || campaign.status === 'SCHEDULED' ? (
              <Button variant="contained" color="warning" size="small" onClick={() => setPauseConfirmOpen(true)}>
                {t('campaignDetail.pause')}
              </Button>
            ) : null}
            {campaign.status === 'PAUSED' ? (
              <Button
                variant="contained"
                size="small"
                onClick={() =>
                  activateMutation.mutate(campaign.id, {
                    onSuccess: () => success(t('campaignDetail.activated')),
                    onError: (e) => notifyError(e.message),
                  })
                }
              >
                {t('campaignDetail.activate')}
              </Button>
            ) : null}
            <IconButton size="small" aria-label={t('campaignDetail.moreActionsAria')} onClick={(e) => setMenuAnchor(e.currentTarget)}>
              <MoreVertRoundedIcon />
            </IconButton>
            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  setEditOpen(true);
                }}
              >
                {t('common:action.edit')}
              </MenuItem>
              {canEnd ? (
                <MenuItem
                  onClick={() => {
                    setMenuAnchor(null);
                    setConfirmAction('end');
                  }}
                >
                  {t('campaignDetail.end')}
                </MenuItem>
              ) : null}
              {campaign.status !== 'ARCHIVED' ? (
                <MenuItem
                  onClick={() => {
                    setMenuAnchor(null);
                    setConfirmAction('archive');
                  }}
                >
                  {t('campaignDetail.archive')}
                </MenuItem>
              ) : null}
            </Menu>
          </Stack>
        ) : null}
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('campaignDetail.period', {
          start: campaign.startsAt ? formatDate(campaign.startsAt) : '—',
          end: campaign.endsAt ? formatDate(campaign.endsAt) : '—',
        })}
      </Typography>

      <Stack spacing={2.5}>
        {campaign.budgetId ? (
          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {t('campaignDetail.budgetCardTitle')}
                </Typography>
                <Button size="small" component={RouterLink} to="/promo/budgets">
                  {t('campaignDetail.openBudgets')}
                </Button>
              </Stack>
              {budgetBurn.data ? <BudgetBurnBar budget={budgetBurn.data} /> : <LoadingState label="" fullHeight={false} />}
            </CardContent>
          </Card>
        ) : null}

        <Card variant="outlined">
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {t('campaignDetail.promotionsCardTitle')}
              </Typography>
              {canManage ? (
                <Button
                  size="small"
                  startIcon={<AddRoundedIcon />}
                  component={RouterLink}
                  to={`/promo/promotions/new?campaignId=${campaign.id}`}
                >
                  {t('campaignDetail.addPromotion')}
                </Button>
              ) : null}
            </Stack>
            {campaignPromotions.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('campaignDetail.noPromotions')}
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('promotionsList.table.name')}</TableCell>
                      <TableCell>{t('promotionsList.table.type')}</TableCell>
                      <TableCell>{t('promotionsList.table.period')}</TableCell>
                      <TableCell align="right">{t('promotionsList.table.priority')}</TableCell>
                      <TableCell>{t('promotionsList.table.status')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {campaignPromotions.map((p) => (
                      <TableRow key={p.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/promo/promotions/${p.id}`)}>
                        <TableCell>{p.name}</TableCell>
                        <TableCell>{t(`builder.type.${p.type}`)}</TableCell>
                        <TableCell>
                          {p.validFrom ? formatDate(p.validFrom) : '—'} – {p.validTo ? formatDate(p.validTo) : '—'}
                        </TableCell>
                        <TableCell align="right">{p.priority}</TableCell>
                        <TableCell>
                          <Chip size="small" color={PROMOTION_STATUS_COLOR[p.status]} label={p.status} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Stack>

      <CampaignFormDialog
        open={editOpen}
        editing={campaign}
        saving={updateMutation.isPending}
        onClose={() => setEditOpen(false)}
        onSubmit={(body) =>
          updateMutation.mutate(
            { campaignId: campaign.id, body },
            {
              onSuccess: () => {
                success(t('campaignDetail.updated'));
                setEditOpen(false);
              },
              onError: (e) => notifyError(e.message),
            },
          )
        }
      />

      <Dialog open={pauseConfirmOpen} onClose={() => setPauseConfirmOpen(false)}>
        <DialogTitle>{t('campaignDetail.pauseDialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('campaignDetail.pauseDialog.body', { count: campaignPromotions.length })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPauseConfirmOpen(false)}>{t('common:action.cancel')}</Button>
          <Button
            color="warning"
            variant="contained"
            onClick={() =>
              pauseMutation.mutate(
                { campaignId: campaign.id },
                {
                  onSuccess: () => {
                    success(t('campaignDetail.paused'));
                    setPauseConfirmOpen(false);
                  },
                  onError: (e) => notifyError(e.message),
                },
              )
            }
          >
            {t('campaignDetail.pause')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmAction === 'end'} onClose={() => setConfirmAction(null)}>
        <DialogTitle>{t('campaignDetail.endDialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('campaignDetail.endDialog.body')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)}>{t('common:action.cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() =>
              endMutation.mutate(campaign.id, {
                onSuccess: () => {
                  success(t('campaignDetail.ended'));
                  setConfirmAction(null);
                },
                onError: (e) => notifyError(e.message),
              })
            }
          >
            {t('campaignDetail.end')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmAction === 'archive'} onClose={() => setConfirmAction(null)}>
        <DialogTitle>{t('campaignDetail.archiveDialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('campaignDetail.archiveDialog.body')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)}>{t('common:action.cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() =>
              archiveMutation.mutate(campaign.id, {
                onSuccess: () => {
                  success(t('campaignDetail.archived'));
                  setConfirmAction(null);
                },
                onError: (e) => notifyError(e.message),
              })
            }
          >
            {t('campaignDetail.archive')}
          </Button>
        </DialogActions>
      </Dialog>

      {!canManage ? <Alert severity="info" sx={{ mt: 2 }}>{t('builder.readOnlyAlert')}</Alert> : null}

      <Snackbar open={toast !== null} autoHideDuration={5000} onClose={clear} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={clear} severity={toast?.severity ?? 'info'} variant="filled" sx={{ width: '100%' }}>
          {toast?.message ?? ''}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
}
